import { httpsCallable } from "firebase/functions";
import {
  collection,
  doc,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import { db, functions } from "../lib/firebase";
import type {
  Audience,
  CreateAudienceInput,
  CreateAudienceResponse,
  UpdateAudienceInput,
  UpdateAudienceResponse,
  DeleteAudienceInput,
  DeleteAudienceResponse,
  BulkCreateAudiencesInput,
  BulkCreateAudiencesResponse,
  BulkResolveAudiencesInput,
  BulkResolveAudiencesResponse,
} from "@event-planner/shared";

/**
 * Thin wrappers around the audience callables (ADR-120 + ADR-137).
 * Direct Firestore writes to `audiences` are blocked by security rules
 * for named audiences, so these callables are the only path; the
 * cascading resolver keeps `audience.resolvedPersonIds` and downstream
 * consumer `targetedPersonIds[]` in sync with audience composition +
 * membership changes.
 *
 * Anonymous compositions (`name == null`) are written from the parent
 * compose flow via Firestore writeBatch, see `resolveAudienceTarget`
 * below.
 */

const createAudienceCallable = httpsCallable<
  CreateAudienceInput,
  CreateAudienceResponse
>(functions, "createAudience");

const updateAudienceCallable = httpsCallable<
  UpdateAudienceInput,
  UpdateAudienceResponse
>(functions, "updateAudience");

const deleteAudienceCallable = httpsCallable<
  DeleteAudienceInput,
  DeleteAudienceResponse
>(functions, "deleteAudience");

const bulkCreateAudiencesCallable = httpsCallable<
  BulkCreateAudiencesInput,
  BulkCreateAudiencesResponse
>(functions, "bulkCreateAudiences");

const bulkResolveAudiencesCallable = httpsCallable<
  BulkResolveAudiencesInput,
  BulkResolveAudiencesResponse
>(functions, "bulkResolveAudiences");

export async function createAudience(
  input: CreateAudienceInput,
): Promise<CreateAudienceResponse> {
  const result = await createAudienceCallable(input);
  return result.data;
}

export async function updateAudience(
  input: UpdateAudienceInput,
): Promise<UpdateAudienceResponse> {
  const result = await updateAudienceCallable(input);
  return result.data;
}

export async function deleteAudience(
  input: DeleteAudienceInput,
): Promise<DeleteAudienceResponse> {
  const result = await deleteAudienceCallable(input);
  return result.data;
}

export async function bulkCreateAudiences(
  input: BulkCreateAudiencesInput,
): Promise<BulkCreateAudiencesResponse> {
  const result = await bulkCreateAudiencesCallable(input);
  return result.data;
}

export async function bulkResolveAudiences(
  input: BulkResolveAudiencesInput,
): Promise<BulkResolveAudiencesResponse> {
  const result = await bulkResolveAudiencesCallable(input);
  return result.data;
}

/**
 * Result of resolving the admin audience picker into a target audience
 * the parent compose flow can attach to its doc.
 *
 *   - `mode: 'reuse'` — exactly one audience matched (named or
 *     personal); the parent doc points at it directly. No batch
 *     operations needed.
 *   - `mode: 'compose'` — multiple audiences picked; the parent's
 *     writeBatch must include the anonymous composition write
 *     described by `audienceRef` + `audienceData`. The parent doc's
 *     `audienceId` should be set to `audienceRef.id`.
 *
 * Under ADR-136 the legacy `pendingContactIds` field is gone: pending
 * people are real persons that surface through their personal audience
 * IDs in `pickedAudienceIds`, so the parent flow does not need a
 * separate staging arm.
 */
export type ResolvedAudienceTarget =
  | {
      mode: "reuse";
      audienceId: string;
    }
  | {
      mode: "compose";
      audienceRef: ReturnType<typeof doc>;
      audienceData: Record<string, unknown>;
    };

/**
 * Take the admin audience picker's selection (audience IDs picked from
 * the picker, including personal audience IDs for individual people)
 * and return the target shape for the parent compose flow.
 *
 * Single-audience case (one picked) → reuse it.
 * Multi-audience case → fresh anonymous composition with includes.
 */
export function resolveAudienceTarget(
  eventId: string,
  pickedAudienceIds: string[],
): ResolvedAudienceTarget {
  const audienceIds = Array.from(new Set(pickedAudienceIds));
  if (audienceIds.length === 1) {
    return {
      mode: "reuse",
      audienceId: audienceIds[0],
    };
  }
  const audienceRef = doc(collection(db, "audiences"));
  return {
    mode: "compose",
    audienceRef,
    audienceData: {
      eventId,
      name: null,
      description: null,
      members: {
        roles: [],
        personIds: [],
        includedAudienceIds: audienceIds,
      },
      // CF onAudienceWritten fills resolvedPersonIds. Empty array is
      // required by the security rule on create (ADR-137).
      resolvedPersonIds: [],
      system: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
  };
}

/**
 * Helper for unit tests + parent services: returns true iff the linked
 * audience is anonymous + non-system, i.e. owned by its parent and a
 * candidate for orphan cleanup when the parent's audienceId changes.
 */
export function isAnonymousAudience(
  audience: { name: string | null; system: boolean } | null | undefined,
): boolean {
  if (!audience) return false;
  return audience.name === null && audience.system === false;
}

/**
 * Reverse-resolve a session's linked audience back into the picker's
 * `pickedAudienceIds` value. Anonymous compositions spread their include
 * list (so the organizer sees what they originally picked, and saving
 * re-resolves via `resolveAudienceTarget` — minting a FRESH composition
 * rather than sharing the source's, which `deleteSession` would delete
 * out from under a sibling). Named/personal audiences resolve to their
 * own single ID. Null/missing audience → `[]` (the form's min(1) forces
 * a repick).
 */
export function pickedAudienceIdsForAudience(
  audience: Audience | null | undefined,
): string[] {
  if (!audience) return [];
  return isAnonymousAudience(audience)
    ? audience.members.includedAudienceIds
    : [audience.id];
}

export { writeBatch };
